import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Save, CheckCircle, Mail, Star, Clock, Tag, CalendarClock, User, FileText } from "lucide-react";
import { generateSlots } from "@/utils/slots";
import axios from "axios";
import RescheduleModal from "@/components/RescheduleModal";
import { Link, useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_STATUSES = [
  { value: "pendiente_confirmacion", label: "Pte. Confirmacion" },
  { value: "confirmada", label: "Confirmada" },
  { value: "pendiente_reprogramacion", label: "Pte. Reprogramacion" },
  { value: "cancelada_cliente", label: "Cancelada (cliente)" },
  { value: "cancelada_taller", label: "Cancelada (taller)" },
];

const DAY_MAP = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

// Iter 150/159 — etiquetas humanas para los eventos del timeline
const EVENT_LABELS = {
  created: "Cita creada",
  updated: "Cita modificada",
  rescheduled: "Cita reprogramada",
  cancelled: "Cita cancelada",
  confirmed: "Cita confirmada",
  status_changed: "Cambio de estado",
  email_sent: "Email enviado al cliente",
  email_failed: "Email NO enviado",
  // Iter 202 (P0) — Solicitud de modificación del cliente procesada por gestión
  modification_request_processed: "Solicitud de modificación procesada",
};

export default function DetailEditForm({ apt, config, onClose, onSave, axiosAuth: axiosAuthProp }) {
  const { axiosAuth: axiosAuthCtx } = useAuth();
  const axiosAuth = axiosAuthProp || axiosAuthCtx;
  // Iter 206 (P0) — navigate de react-router en MISMA pestaña para evitar el bug
  // donde window.open lanzaba una pestaña nueva sin sesión activa y redirigía a /login.
  const navigate = useNavigate();
  const [f, setF] = useState({
    client_name: apt.client_name || "", client_phone: apt.client_phone || "", client_email: apt.client_email || "",
    plate: apt.plate || "", brand: apt.brand || "", model_name: apt.model_name || "",
    center: apt.center || "", center_name: apt.center_name || "",
    date: apt.date || "", time_slot: apt.time_slot || "",
    service_type: apt.service_type || "", service_description: apt.service_description || "",
    advisor_id: apt.advisor_id || "", advisor_name: apt.advisor_name || "",
    status: apt.status || "",
    replacement_vehicle_needed: apt.replacement_vehicle_needed || "no",
    replacement_vehicle_notes: apt.replacement_vehicle_notes || "",
    replacement_vehicle_status: apt.replacement_vehicle_status || "not_needed",
    // Iter 154 — campos de gestión interna del vehículo de alquiler
    rental_vehicle_status: apt.rental_vehicle_status || "",
    rental_vehicle_reserved: apt.rental_vehicle_reserved || "",
    rental_pickup_time: apt.rental_pickup_time || "",
    rental_return_time: apt.rental_return_time || "",
    rental_manager: apt.rental_manager || "",
    rental_internal_notes: apt.rental_internal_notes || "",
    // Iter 156 — IDs del catálogo (snapshot del texto vive en *_reserved / *_manager)
    rental_vehicle_reserved_id: apt.rental_vehicle_reserved_id || "",
    rental_manager_id: apt.rental_manager_id || "",
  });
  // Iter 156 — modo manual ("Otro / escribir manualmente") vs selector de catálogo
  const [rentalVehicleManual, setRentalVehicleManual] = useState(
    !apt.rental_vehicle_reserved_id && !!apt.rental_vehicle_reserved
  );
  const [rentalManagerManual, setRentalManagerManual] = useState(
    !apt.rental_manager_id && !!apt.rental_manager
  );
  // Iter 156 — catálogos cargados desde /api/rental-catalog/*
  const [rentalVehiclesCatalog, setRentalVehiclesCatalog] = useState([]);
  const [rentalContactsCatalog, setRentalContactsCatalog] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [centers, setCenters] = useState([]);
  const [clientHistory, setClientHistory] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  // Iter 150 — historial automático + notas internas del cliente
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [internalNotes, setInternalNotes] = useState({ notes: "", updated_at: "", updated_by_name: "", can_edit: false, customer_kind: "", customer_key: "" });
  const [internalNotesDraft, setInternalNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Cargar historial + notas internas del cliente cada vez que cambia la cita
  // Iter 152 — incluir `apt?.updated_at` en la dependencia para refetch tras guardar
  useEffect(() => {
    if (!apt?.id || !axiosAuth) return;
    let cancelled = false;
    axiosAuth().get(`/appointments/${apt.id}/history`).then(r => {
      if (cancelled) return;
      setHistory(r.data?.items || []);
    }).catch(() => setHistory([]));
    axiosAuth().get(`/appointments/${apt.id}/customer-notes`).then(r => {
      if (cancelled) return;
      const d = r.data || {};
      setInternalNotes(d);
      setInternalNotesDraft(d.notes || "");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [apt?.id, apt?.updated_at, axiosAuth]);

  // Iter 152 — refetch explícito del historial (usado tras guardar cambios)
  const refetchHistory = useCallback(async () => {
    if (!apt?.id || !axiosAuth) return;
    try {
      const r = await axiosAuth().get(`/appointments/${apt.id}/history`);
      setHistory(r.data?.items || []);
    } catch { /* ignore */ }
  }, [apt?.id, axiosAuth]);

  const saveInternalNotes = async () => {
    if (!axiosAuth) return;
    setSavingNotes(true);
    try {
      const r = await axiosAuth().put(`/appointments/${apt.id}/customer-notes`, { notes: internalNotesDraft });
      setInternalNotes(r.data);
      setInternalNotesDraft(r.data.notes || "");
      toast.success("Notas internas guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando notas");
    } finally {
      setSavingNotes(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/centers`).then(r => setCenters(r.data)).catch(() => {});
  }, []);

  // Iter 156 — cargar catálogos de alquiler para los selectores
  useEffect(() => {
    if (!axiosAuth) return;
    axiosAuth().get("/rental-catalog/vehicles")
      .then(r => setRentalVehiclesCatalog(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRentalVehiclesCatalog([]));
    axiosAuth().get("/rental-catalog/contacts")
      .then(r => setRentalContactsCatalog(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRentalContactsCatalog([]));
  }, [axiosAuth]);

  useEffect(() => {
    if (!f.center) return;
    axios.get(`${API}/advisors/by-center/${f.center}`).then(r => setAdvisors(r.data)).catch(() => setAdvisors([]));
  }, [f.center]);

  // Fetch client history
  useEffect(() => {
    if (!apt.client_phone && !apt.plate && !apt.client_email) return;
    const params = new URLSearchParams();
    if (apt.client_phone) params.set("phone", apt.client_phone);
    if (apt.plate) params.set("plate", apt.plate);
    if (apt.client_email) params.set("email", apt.client_email);
    if (axiosAuth) {
      axiosAuth().get(`/appointments/client-history?${params}`).then(r => setClientHistory(r.data)).catch(() => {});
    }
  }, [apt.client_phone, apt.plate, apt.client_email, axiosAuth]);

  // Fetch availability when date/center changes
  useEffect(() => {
    if (!f.date || !f.center) { setAvailability([]); return; }
    const advParam = f.advisor_id ? `&advisor_id=${f.advisor_id}` : "";
    axios.get(`${API}/appointments/availability?date=${f.date}&center=${f.center}${advParam}`)
      .then(r => setAvailability(r.data.slots || []))
      .catch(() => setAvailability([]));
  }, [f.date, f.center, f.advisor_id]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const changed = Object.keys(f).some(k => f[k] !== (apt[k] || ""));

  // Time slots
  const workingHours = config?.working_hours || {};
  const interval = config?.appointment_interval || 20;
  const dayKey = f.date ? DAY_MAP[new Date(f.date + "T12:00:00").getDay()] : "";
  const dayRange = workingHours[dayKey];
  const allSlots = generateSlots(dayRange, interval);

  const slotData = allSlots.map(s => {
    const info = availability.find(a => a.slot === s);
    return { slot: s, available: info ? info.available : true };
  });

  // Habitual client threshold
  const habitualThreshold = config?.habitual_client_threshold || 3;
  const isHabitual = clientHistory && clientHistory.total_visits >= habitualThreshold;

  // Iter 202 (P0) — Quick confirm que respeta cambios de gestor.
  // Antes solo enviaba {status:"confirmada"}, perdiendo los cambios de date/time_slot
  // que el gestor pudiera haber hecho en el modal → el email salía con la hora vieja.
  // Ahora envía TODOS los cambios pendientes + status="confirmada" en una sola llamada.
  const handleQuickConfirm = async () => {
    setConfirming(true);
    try {
      const updates = { status: "confirmada" };
      Object.keys(f).forEach(k => {
        if (f[k] !== (apt[k] || "")) updates[k] = f[k];
      });
      await onSave(updates);
      const wasReprog = apt.status === "pendiente_reprogramacion";
      const dateChanged = f.date && f.date !== (apt.date || "");
      const timeChanged = f.time_slot && f.time_slot !== (apt.time_slot || "");
      if (wasReprog || dateChanged || timeChanged) {
        toast.success("Reprogramación confirmada y email enviado al cliente.");
      } else {
        toast.success("Cita confirmada y email enviado al cliente.");
      }
      onClose && onClose();
    } catch {
      toast.error("Error al confirmar la cita. Revisa los datos.");
    }
    setConfirming(false);
  };

  // Iter 202 (P0) — Helper: detecta si hay diferencia entre lo solicitado por el
  // cliente (modification_request) y lo que el gestor va a confirmar (f.date/f.time_slot).
  const modReq = apt.modification_request;
  const reqDate = modReq?.new_date || "";
  const reqTime = modReq?.new_time_slot || "";
  const dateDiffers = modReq && reqDate && f.date && reqDate !== f.date;
  const timeDiffers = modReq && reqTime && f.time_slot && reqTime !== f.time_slot;
  const showDiffWarning = modReq && (dateDiffers || timeDiffers);
  const formatDateES = (iso) => {
    if (!iso) return "";
    const p = String(iso).split("-");
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
  };

  // Send custom email handler
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error("Completa asunto y mensaje");
      return;
    }
    setSendingEmail(true);
    try {
      await axiosAuth().post("/appointments/send-email", {
        appointment_id: apt.id,
        subject: emailSubject,
        message: emailMessage,
      });
      toast.success("Email enviado al cliente");
      setShowEmailModal(false);
      setEmailSubject("");
      setEmailMessage("");
      // Iter 159 — actualizar historial al instante para reflejar el evento email_sent
      await refetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar email");
    }
    setSendingEmail(false);
  };

  return (
    <div className="space-y-4 py-2">
      {/* Iter 202 (P0) — Solicitud de modificación del cliente: ubicación SUPERIOR,
          junto a fecha/hora. Compara claramente lo solicitado por el cliente vs
          lo que el gestor confirmará. Si hay diferencia, aviso suave indicando
          que el email saldrá con la confirmación de gestión. */}
      {apt.modification_request && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-sm p-3 space-y-2" data-testid="modification-request-comparison">
          <p className="text-xs uppercase tracking-wider font-bold text-purple-900 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Solicitud de modificación del cliente
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/70 border border-purple-200 rounded-sm p-2 space-y-1" data-testid="mod-req-client">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-purple-700">Solicitud del cliente</p>
              {reqDate && (
                <p className="text-xs text-purple-900">
                  Fecha solicitada: <strong className="font-mono">{formatDateES(reqDate)}</strong>
                </p>
              )}
              {reqTime && (
                <p className="text-xs text-purple-900">
                  Hora solicitada: <strong className="font-mono">{reqTime}</strong>
                </p>
              )}
              {modReq.comment && (
                <p className="text-[11px] text-purple-700 italic break-words">"{modReq.comment}"</p>
              )}
            </div>
            <div className="bg-white/70 border border-emerald-200 rounded-sm p-2 space-y-1" data-testid="mod-req-manager">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Confirmación de gestión</p>
              <p className="text-xs text-slate-900">
                Fecha que se confirmará: <strong className={"font-mono " + (dateDiffers ? "text-amber-700" : "text-emerald-700")}>{formatDateES(f.date)}</strong>
              </p>
              <p className="text-xs text-slate-900">
                Hora que se confirmará: <strong className={"font-mono " + (timeDiffers ? "text-amber-700" : "text-emerald-700")}>{f.time_slot}</strong>
              </p>
            </div>
          </div>
          {showDiffWarning && (
            <div className="bg-amber-50 border border-amber-300 rounded-sm p-2 flex items-start gap-2" data-testid="mod-req-diff-warning">
              <RefreshCw className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-900 leading-snug">
                <strong>La fecha u hora confirmada por gestión es distinta a la solicitada por el cliente.</strong>{" "}
                El email se enviará con la fecha y hora finalmente confirmadas.
              </p>
            </div>
          )}
          {/* Botón rápido para adoptar la solicitud del cliente tal cual */}
          {(reqDate !== f.date || reqTime !== f.time_slot) && (reqDate || reqTime) && (
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => {
                if (reqDate) set("date", reqDate);
                if (reqTime) set("time_slot", reqTime);
                toast.info("Aplicada la fecha/hora solicitada por el cliente. Pulsa Confirmar para enviar.");
              }}
              className="rounded-sm text-[10px] uppercase font-semibold border-purple-300 text-purple-800 hover:bg-purple-100"
              data-testid="mod-req-apply-client-btn"
            >
              Aplicar lo solicitado por el cliente
            </Button>
          )}
        </div>
      )}

      {/* Origen: Lead histórico (Iter 163) */}
      {apt.source === "legacy_lead" && apt.legacy_lead_id && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 flex items-start gap-3 min-w-0" data-testid="appointment-legacy-origin">
          <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-indigo-900 space-y-1.5 flex-1 min-w-0">
            <p className="font-bold uppercase tracking-wide text-xs">Origen: Lead histórico</p>
            <div className="flex items-center gap-2 flex-wrap">
              {apt.legacy_form_name && (
                <span className="text-[10px] uppercase tracking-wider text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-sm break-all">
                  {apt.legacy_form_name}
                </span>
              )}
              {apt.legacy_original_date && (
                <span className="text-[10px] text-indigo-600">
                  Original: {String(apt.legacy_original_date).slice(0, 10)}
                </span>
              )}
              {apt.converted_by_email && (
                <span className="text-[10px] text-indigo-600 break-all">Por: {apt.converted_by_email}</span>
              )}
              {apt.converted_at && (
                <span className="text-[10px] text-indigo-600">{new Date(apt.converted_at).toLocaleString("es-ES")}</span>
              )}
            </div>
            <div className="pt-1">
              <Link
                to={`/admin/leads-historicos?lead=${apt.legacy_lead_id}`}
                className="text-[10px] uppercase tracking-wider inline-flex items-center px-2 py-1 rounded-sm bg-indigo-700 text-white hover:bg-indigo-800 max-w-full"
                data-testid="appointment-legacy-link"
              >
                Ver lead original
              </Link>
            </div>
          </div>
        </div>
      )}
      {/* Promotion info */}
      {apt.promo && apt.promo.id && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 flex items-start gap-3" data-testid="appointment-promo-info">
          <Tag className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-red-900 space-y-1">
            <p className="font-bold uppercase tracking-wide text-xs">Promocion vinculada</p>
            <p className="text-sm font-bold text-slate-900">{apt.promo.titulo}</p>
            <div className="flex items-center gap-3 flex-wrap mt-1">
              {apt.promo.precio && <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-sm">{apt.promo.precio}</span>}
              {apt.promo.categoria && <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 px-1.5 py-0.5 rounded-sm">{apt.promo.categoria}</span>}
              <span className="text-[10px] text-slate-400">Origen: {apt.promo.origen || apt.source}</span>
              <span className="text-[10px] text-slate-400 font-mono">ID: {apt.promo.id}</span>
            </div>
          </div>
        </div>
      )}
      {/* VIP Client indicator */}
      {clientHistory && clientHistory.is_vip && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-3" data-testid="vip-client-badge">
          <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-amber-800 space-y-1">
            <p><strong>CLIENTE VIP</strong> &mdash; {clientHistory.total_visits} visitas</p>
            {clientHistory.last_visit && (
              <p>Ultima visita: {(() => { const p = clientHistory.last_visit.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : clientHistory.last_visit.date; })()}
                {clientHistory.last_visit.service_type && <> &middot; {clientHistory.last_visit.service_type}</>}
              </p>
            )}
            <div className="flex gap-3 flex-wrap">
              {clientHistory.usual_center && <span className="text-[10px] bg-amber-100 px-1.5 py-0.5 rounded-sm">Centro: {clientHistory.usual_center}</span>}
              {clientHistory.usual_advisor && <span className="text-[10px] bg-amber-100 px-1.5 py-0.5 rounded-sm">Asesor: {clientHistory.usual_advisor}</span>}
            </div>
          </div>
        </div>
      )}
      {/* Habitual client (not VIP) */}
      {clientHistory && !clientHistory.is_vip && isHabitual && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-2.5 flex items-start gap-3" data-testid="habitual-client-badge">
          <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div className="text-[11px] text-blue-800 space-y-0.5">
            <p><strong>Cliente habitual</strong> &mdash; {clientHistory.total_visits} visitas</p>
            {clientHistory.last_visit && (
              <p>Ultima: {(() => { const p = clientHistory.last_visit.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : clientHistory.last_visit.date; })()}
                {clientHistory.last_visit.service_type && <> &middot; {clientHistory.last_visit.service_type}</>}
              </p>
            )}
            <div className="flex gap-3 flex-wrap">
              {clientHistory.usual_center && <span className="text-[10px] bg-blue-100 px-1.5 py-0.5 rounded-sm">Centro: {clientHistory.usual_center}</span>}
              {clientHistory.usual_advisor && <span className="text-[10px] bg-blue-100 px-1.5 py-0.5 rounded-sm">Asesor: {clientHistory.usual_advisor}</span>}
            </div>
          </div>
        </div>
      )}
      {/* New client or few visits */}
      {clientHistory && !isHabitual && clientHistory.total_visits > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-2 flex items-center gap-2" data-testid="client-visits-info">
          <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-[11px] text-slate-600">{clientHistory.total_visits} visita{clientHistory.total_visits > 1 ? 's' : ''}
            {clientHistory.last_visit && <> &middot; Ultima: {(() => { const p = clientHistory.last_visit.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : clientHistory.last_visit.date; })()}</>}
            {clientHistory.usual_center && <> &middot; Centro: {clientHistory.usual_center}</>}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Cliente</Label>
          <Input value={f.client_name} onChange={e => set("client_name", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="detail-client-name" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Telefono</Label>
          <Input value={f.client_phone} onChange={e => set("client_phone", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="detail-client-phone" />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Email</Label>
          <Input value={f.client_email} onChange={e => set("client_email", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="detail-client-email" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Matricula</Label>
          <Input value={f.plate} onChange={e => set("plate", e.target.value.toUpperCase())} className="rounded-sm h-8 text-sm font-mono" data-testid="detail-plate" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vehiculo</Label>
          <div className="flex gap-1">
            <Input value={f.brand} onChange={e => set("brand", e.target.value)} className="rounded-sm h-8 text-sm flex-1" placeholder="Marca" data-testid="detail-brand" />
            <Input value={f.model_name} onChange={e => set("model_name", e.target.value)} className="rounded-sm h-8 text-sm flex-1" placeholder="Modelo" data-testid="detail-model" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Centro</Label>
          <Select value={f.center} onValueChange={v => { const c = centers.find(x => (x.slug || x.id) === v); set("center", v); set("center_name", c?.name || v); set("advisor_id", ""); set("advisor_name", ""); }}>
            <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="detail-center"><SelectValue /></SelectTrigger>
            <SelectContent>{centers.map(c => <SelectItem key={c.slug || c.id} value={c.slug || c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Asesor</Label>
          <Select value={f.advisor_id || "_none"} onValueChange={v => { if (v === "_none") { set("advisor_id", ""); set("advisor_name", ""); } else { const a = advisors.find(x => x.id === v); set("advisor_id", v); set("advisor_name", a?.name || ""); } }}>
            <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="detail-advisor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Sin asignar</SelectItem>
              {advisors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Fecha</Label>
          <Input type="date" value={f.date} onChange={e => set("date", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="detail-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Hora</Label>
          <div className="relative">
            <button type="button" onClick={() => setShowSlotPicker(!showSlotPicker)}
              className="w-full h-8 rounded-sm border border-slate-200 px-3 text-sm font-mono text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
              data-testid="detail-time-btn">
              {f.time_slot || "Seleccionar..."}
              <Clock className="w-3 h-3 text-slate-400" />
            </button>
            {showSlotPicker && allSlots.length > 0 && (
              <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg p-1.5 max-h-[180px] overflow-y-auto" data-testid="slot-picker-dropdown">
                <div className="grid grid-cols-4 gap-px">
                  {slotData.map(s => (
                    <button key={s.slot} type="button" disabled={!s.available}
                      onClick={() => { set("time_slot", s.slot); setShowSlotPicker(false); }}
                      className={`text-[10px] font-mono py-1.5 rounded-sm transition-colors ${
                        f.time_slot === s.slot ? 'bg-red-600 text-white font-bold' :
                        !s.available ? 'bg-slate-100 text-slate-300 cursor-not-allowed line-through' :
                        'bg-white text-slate-700 hover:bg-blue-50'
                      }`}
                      data-testid={`detail-slot-${s.slot}`}>
                      {s.slot}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Servicio</Label>
          <Input value={f.service_type} onChange={e => set("service_type", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="detail-service" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Estado</Label>
          <Select value={f.status} onValueChange={v => set("status", v)}>
            <SelectTrigger className="rounded-sm h-8 text-sm" data-testid="detail-status"><SelectValue /></SelectTrigger>
            <SelectContent>{ADMIN_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {/* Iter 223 — Bloque "Servicios seleccionados por el cliente".
          Muestra todos los servicios de apt.services como chips para que el
          gestor vea exactamente qué pidió el cliente, no solo el primero. */}
      {(() => {
        const list = (apt?.services || [])
          .map(s => (typeof s === "string" ? s : (s?.name || "")))
          .filter(Boolean);
        if (list.length === 0) return null;
        return (
          <div className="space-y-1.5" data-testid="detail-services-block">
            <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Servicios seleccionados por el cliente
            </Label>
            <ul className="flex flex-wrap gap-1.5" data-testid="detail-services-chips">
              {list.map((name, i) => (
                <li
                  key={`${name}-${i}`}
                  className="px-2 py-1 rounded-sm bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium"
                  data-testid={`detail-service-chip-${i}`}
                >
                  {name}
                </li>
              ))}
            </ul>
            {list.length > 1 && (
              <p className="text-[10px] text-slate-500 italic" data-testid="detail-services-hint">
                {list.length} servicios solicitados por el cliente.
              </p>
            )}
          </div>
        );
      })()}
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Descripcion servicio</Label>
        <Textarea value={f.service_description} onChange={e => set("service_description", e.target.value)} className="rounded-sm text-sm h-16 resize-none" data-testid="detail-description" />
      </div>

      {/* Iter 154 — Bloque Vehículo de Alquiler (antes "sustitución") */}
      <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-3 space-y-3" data-testid="detail-rv-block">
        <p className="text-xs uppercase tracking-wider font-bold text-amber-800 flex items-center gap-1">
          Vehículo de alquiler
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Necesidad indicada por el cliente</Label>
            <Select value={f.replacement_vehicle_needed} onValueChange={(v) => set("replacement_vehicle_needed", v)}>
              <SelectTrigger className="h-8 rounded-sm text-sm border-slate-200" data-testid="detail-rv-needed">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Sí</SelectItem>
                <SelectItem value="unknown">Consultar (cliente solicita llamada)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Estado interno de gestión</Label>
            <Select value={f.replacement_vehicle_status} onValueChange={(v) => set("replacement_vehicle_status", v)}>
              <SelectTrigger className="h-8 rounded-sm text-sm border-slate-200" data-testid="detail-rv-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_review">Pendiente de revisar</SelectItem>
                <SelectItem value="requested">Solicitado</SelectItem>
                <SelectItem value="confirmed">Confirmado</SelectItem>
                <SelectItem value="not_available">No disponible</SelectItem>
                <SelectItem value="not_needed">No necesario</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Comentarios del cliente</Label>
          <Textarea value={f.replacement_vehicle_notes} onChange={e => set("replacement_vehicle_notes", e.target.value)}
            placeholder="Sin comentarios"
            className="rounded-sm text-sm h-14 resize-none" data-testid="detail-rv-notes" />
        </div>

        {/* Iter 154 — Sub-bloque gestión interna (solo si hay solicitud o consulta) */}
        {(f.replacement_vehicle_needed === "yes" || f.replacement_vehicle_needed === "unknown") && (
          <div className="border-t border-amber-300 pt-3 mt-1 space-y-3" data-testid="detail-rental-internal-block">
            <p className="text-[10px] uppercase tracking-wider font-bold text-amber-900 flex items-center gap-1">
              Gestión interna del vehículo de alquiler
              <span className="ml-1 normal-case font-normal text-amber-700">(no se envía al cliente)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Estado gestión</Label>
                <Select value={f.rental_vehicle_status || ""} onValueChange={(v) => set("rental_vehicle_status", v)}>
                  <SelectTrigger className="h-8 rounded-sm text-sm border-slate-200 bg-white" data-testid="detail-rental-status">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente de confirmar</SelectItem>
                    <SelectItem value="solicitado">Solicitado</SelectItem>
                    <SelectItem value="reservado">Reservado</SelectItem>
                    <SelectItem value="entregado">Entregado</SelectItem>
                    <SelectItem value="devuelto">Devuelto</SelectItem>
                    <SelectItem value="no_disponible">No disponible</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vehículo reservado</Label>
                {!rentalVehicleManual ? (
                  <Select
                    value={f.rental_vehicle_reserved_id || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__manual__") {
                        setRentalVehicleManual(true);
                        set("rental_vehicle_reserved_id", "");
                        return;
                      }
                      if (v === "__none__") {
                        set("rental_vehicle_reserved_id", "");
                        set("rental_vehicle_reserved", "");
                        return;
                      }
                      const cat = rentalVehiclesCatalog.find(x => x.id === v);
                      const label = cat ? (cat.plate ? `${cat.display_name} - ${cat.plate}` : cat.display_name) : "";
                      set("rental_vehicle_reserved_id", v);
                      set("rental_vehicle_reserved", label);
                    }}
                  >
                    <SelectTrigger className="h-8 rounded-sm text-sm border-slate-200 bg-white" data-testid="detail-rental-reserved-select">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin asignar</SelectItem>
                      {rentalVehiclesCatalog.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.plate ? `${v.display_name} · ${v.plate}` : v.display_name}
                          {v.category ? ` (${v.category})` : ""}
                        </SelectItem>
                      ))}
                      {/* Iter 161 — Si la cita apunta a un id que ya no está activo,
                          mostrar el snapshot guardado etiquetado como (desactivado) */}
                      {f.rental_vehicle_reserved_id
                        && !rentalVehiclesCatalog.some(x => x.id === f.rental_vehicle_reserved_id) && (
                        <SelectItem
                          key="__inactive__"
                          value={f.rental_vehicle_reserved_id}
                          data-testid="detail-rental-reserved-inactive-option"
                        >
                          {f.rental_vehicle_reserved || "Vehículo asignado"} (desactivado)
                        </SelectItem>
                      )}
                      <SelectItem value="__manual__">✎ Otro / escribir manualmente</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-1">
                    <Input value={f.rental_vehicle_reserved || ""}
                      onChange={e => { set("rental_vehicle_reserved", e.target.value); set("rental_vehicle_reserved_id", ""); }}
                      placeholder="Ej: 1234-ABC · Citroën C3"
                      className="h-8 rounded-sm text-sm bg-white flex-1" data-testid="detail-rental-reserved" />
                    <Button type="button" variant="ghost" size="sm"
                      className="h-8 px-2 text-[10px]" data-testid="detail-rental-reserved-from-catalog"
                      onClick={() => { setRentalVehicleManual(false); set("rental_vehicle_reserved", ""); }}>
                      Catálogo
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hora recogida</Label>
                <Input value={f.rental_pickup_time || ""} onChange={e => set("rental_pickup_time", e.target.value)}
                  placeholder="Ej: 08:30"
                  className="h-8 rounded-sm text-sm bg-white" data-testid="detail-rental-pickup" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hora devolución</Label>
                <Input value={f.rental_return_time || ""} onChange={e => set("rental_return_time", e.target.value)}
                  placeholder="Ej: 18:00"
                  className="h-8 rounded-sm text-sm bg-white" data-testid="detail-rental-return" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Persona de contacto</Label>
                {!rentalManagerManual ? (
                  <Select
                    value={f.rental_manager_id || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__manual__") {
                        setRentalManagerManual(true);
                        set("rental_manager_id", "");
                        return;
                      }
                      if (v === "__none__") {
                        set("rental_manager_id", "");
                        set("rental_manager", "");
                        return;
                      }
                      const cat = rentalContactsCatalog.find(x => x.id === v);
                      set("rental_manager_id", v);
                      set("rental_manager", cat ? cat.display_name : "");
                    }}
                  >
                    <SelectTrigger className="h-8 rounded-sm text-sm border-slate-200 bg-white" data-testid="detail-rental-manager-select">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Sin asignar</SelectItem>
                      {rentalContactsCatalog.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.display_name}{c.center ? ` · ${c.center}` : ""}
                        </SelectItem>
                      ))}
                      {/* Iter 161 — id no presente en activos → mostrar snapshot etiquetado */}
                      {f.rental_manager_id
                        && !rentalContactsCatalog.some(x => x.id === f.rental_manager_id) && (
                        <SelectItem
                          key="__inactive__"
                          value={f.rental_manager_id}
                          data-testid="detail-rental-manager-inactive-option"
                        >
                          {f.rental_manager || "Contacto asignado"} (desactivado)
                        </SelectItem>
                      )}
                      <SelectItem value="__manual__">✎ Otro / escribir manualmente</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-1">
                    <Input value={f.rental_manager || ""}
                      onChange={e => { set("rental_manager", e.target.value); set("rental_manager_id", ""); }}
                      placeholder="Persona responsable de la gestión"
                      className="h-8 rounded-sm text-sm bg-white flex-1" data-testid="detail-rental-manager" />
                    <Button type="button" variant="ghost" size="sm"
                      className="h-8 px-2 text-[10px]" data-testid="detail-rental-manager-from-catalog"
                      onClick={() => { setRentalManagerManual(false); set("rental_manager", ""); }}>
                      Catálogo
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                Notas internas de alquiler
                <span className="ml-1 normal-case font-normal text-amber-700">· privadas, jamás al cliente</span>
              </Label>
              <Textarea value={f.rental_internal_notes || ""} onChange={e => set("rental_internal_notes", e.target.value)}
                placeholder="Observaciones internas del equipo de alquiler"
                className="rounded-sm text-sm h-16 resize-none bg-white" data-testid="detail-rental-internal-notes"
                maxLength={2000} />
            </div>
          </div>
        )}
      </div>
      {/* Iter 202 — cuadro morado MOVIDO a la parte superior del modal */}
      {/* Iter 150 — Notas internas del cliente (persistentes, por cliente, no por cita) */}
      <div className="border border-amber-200 bg-amber-50/40 rounded-sm p-3 space-y-2" data-testid="customer-internal-notes">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider font-semibold text-amber-800 flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5" /> Notas internas del cliente
          </p>
          {internalNotes.updated_at && (
            <p className="text-[10px] text-slate-500">
              Última edición: {internalNotes.updated_at.slice(0, 16).replace("T", " ")} · {internalNotes.updated_by_name || "—"}
            </p>
          )}
        </div>
        <p className="text-[10px] text-slate-500">
          Estas notas se muestran cada vez que abras una cita de este cliente. No se envían en emails ni WhatsApp.
          {internalNotes.customer_kind === "phone" && " Vinculadas por teléfono."}
          {internalNotes.customer_kind === "email" && " Vinculadas por email."}
          {internalNotes.customer_kind === "customer_id" && " Vinculadas a ficha de cliente."}
        </p>
        <Textarea
          value={internalNotesDraft}
          onChange={(e) => setInternalNotesDraft(e.target.value)}
          disabled={!internalNotes.can_edit}
          placeholder={internalNotes.can_edit ? "Ej: Cliente habitual, siempre pide vehículo de alquiler." : "Sin permisos para editar"}
          className="rounded-sm text-sm min-h-[80px] resize-none bg-white"
          data-testid="customer-internal-notes-textarea"
          maxLength={2000}
        />
        {internalNotes.can_edit && (
          <Button
            onClick={saveInternalNotes}
            disabled={savingNotes || internalNotesDraft === (internalNotes.notes || "")}
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs uppercase font-semibold h-8"
            data-testid="customer-internal-notes-save-btn"
          >
            <Save className="w-3 h-3 mr-1" /> {savingNotes ? "Guardando..." : "Guardar notas internas"}
          </Button>
        )}
      </div>

      {/* Iter 150 — Historial completo de la cita (colapsable) */}
      <div className="border border-slate-200 rounded-sm" data-testid="appointment-history-section">
        <button
          type="button"
          onClick={() => setHistoryOpen(!historyOpen)}
          className="w-full px-3 py-2 flex items-center justify-between text-left"
          data-testid="appointment-history-toggle"
        >
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Historial de cambios
            <span className="ml-1.5 text-[10px] text-slate-400 normal-case">
              ({history.length} {history.length === 1 ? "evento" : "eventos"})
            </span>
          </span>
          <span className="text-slate-400 text-xs">{historyOpen ? "▲" : "▼"}</span>
        </button>
        {historyOpen && (
          <div className="border-t border-slate-200 p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="appointment-history-list">
            {history.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2 text-center">Sin cambios registrados</p>
            ) : (
              history.map((ev) => (
                <div key={ev.id} className="flex gap-2 text-xs" data-testid={`history-event-${ev.event_type}`}>
                  <div className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[10px] text-slate-400 font-mono">
                        {ev.created_at?.slice(0, 16).replace("T", " ")}
                      </span>
                      <span className={
                        "text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-semibold " +
                        (ev.source === "client" ? "bg-blue-100 text-blue-800"
                          : ev.source === "system" ? "bg-slate-100 text-slate-600"
                          : "bg-emerald-100 text-emerald-800")
                      }>
                        {ev.source === "client" ? "Cliente" : ev.source === "system" ? "Sistema" : "Taller"}
                      </span>
                      <span className="text-[10px] text-slate-500">{ev.user_name || "—"}</span>
                    </div>
                    <p className="text-xs text-slate-800 font-medium mt-0.5">
                      {EVENT_LABELS[ev.event_type] || ev.event_type}
                      {ev.changed_fields?.length > 0 && (
                        <span className="text-slate-500 font-normal"> · {ev.changed_fields.join(", ")}</span>
                      )}
                    </p>
                    {ev.old_value && Object.keys(ev.old_value || {}).length > 0 && (
                      <div className="mt-0.5 text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                        {Object.keys(ev.new_value || {}).map(field => (
                          <span key={field}>
                            <strong>{field}:</strong>{" "}
                            <span className="line-through text-slate-400">{String(ev.old_value?.[field] ?? "—")}</span>
                            {" "}→{" "}
                            <strong className="text-slate-700">{String(ev.new_value?.[field] ?? "—")}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                    {ev.note && (
                      <p className="text-[10px] text-slate-400 italic mt-0.5">"{ev.note}"</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <DialogFooter className="gap-2 flex-wrap">
        <div className="flex gap-2 mr-auto">
          {f.status !== "confirmada" && (
            <Button onClick={handleQuickConfirm} disabled={confirming}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-sm text-xs uppercase font-semibold" data-testid="detail-confirm-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> {confirming ? "Confirmando..." : "Confirmar Cita"}
            </Button>
          )}
          <Button variant="outline" onClick={() => {
            if (!f.client_email) { toast.error("El cliente no tiene email"); return; }
            setShowEmailModal(true);
          }} className="rounded-sm text-xs uppercase" data-testid="detail-send-email-btn">
            <Mail className="w-3 h-3 mr-1" /> Enviar Email
          </Button>
          <Button variant="outline" onClick={() => setShowReschedule(true)}
            className="rounded-sm text-xs uppercase border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="detail-reschedule-btn">
            <CalendarClock className="w-3 h-3 mr-1" /> Reprogramar cita
          </Button>
          {/* Iter 159 — Ficha de cliente · Iter 206 (P0) fix navegación */}
          <Button variant="outline" onClick={async () => {
            // Sin datos de contacto NO podemos resolver la ficha
            if (!f.client_phone && !f.client_email) {
              toast.error("No hay ficha de cliente vinculada (sin teléfono ni email)");
              return;
            }
            try {
              const r = await axiosAuth().get("/customers/lookup", {
                params: { phone: f.client_phone, email: f.client_email },
              });
              if (!r.data?.key) {
                toast.error("No se encontró ficha del cliente vinculada a estos datos");
                return;
              }
              // Cerrar el modal actual y navegar en la MISMA pestaña con react-router.
              // window.open creaba pestaña nueva que perdía la sesión y redirigía a /login.
              onClose && onClose();
              navigate(`/admin/clientes/${encodeURIComponent(r.data.key)}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "No se pudo abrir la ficha del cliente");
            }
          }} className="rounded-sm text-xs uppercase border-slate-300" data-testid="detail-view-customer-btn">
            <User className="w-3 h-3 mr-1" /> Ver ficha cliente
          </Button>
        </div>
        <Button variant="outline" onClick={onClose} className="rounded-sm text-xs uppercase" data-testid="detail-close-btn">Cerrar</Button>
        <Button onClick={async () => {
          const updates = {};
          Object.keys(f).forEach(k => { if (f[k] !== (apt[k] || "")) updates[k] = f[k]; });
          if (Object.keys(updates).length === 0) { toast.info("No hay cambios"); return; }
          // Iter 152 — onSave puede devolver una promesa; tras resolver refrescamos historial.
          // Iter 202 (P0) — Si el guardado cambia el status a 'confirmada' o
          // toca date/time_slot en una cita con modification_request pendiente,
          // mostramos toast acorde y cerramos modal (consistencia con Confirmar).
          try {
            await onSave(updates);
            const dateChanged = "date" in updates;
            const timeChanged = "time_slot" in updates;
            const statusChanged = "status" in updates;
            const becameConfirmed = updates.status === "confirmada";
            if (becameConfirmed) {
              toast.success("Cita confirmada y email enviado al cliente.");
              onClose && onClose();
            } else if (apt.status === "pendiente_reprogramacion" && (dateChanged || timeChanged)) {
              toast.success("Reprogramación guardada. Confirma para enviar email al cliente.");
            } else if (statusChanged || dateChanged || timeChanged) {
              toast.success("Cambios guardados correctamente.");
            }
            refetchHistory();
          } catch { /* el parent ya muestra toast.error */ }
        }}
          disabled={!changed} className="bg-red-600 hover:bg-red-700 rounded-sm text-xs uppercase font-semibold" data-testid="detail-save-btn">
          <Save className="w-3 h-3 mr-1" /> Guardar cambios
        </Button>
      </DialogFooter>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>  <DialogContent className="max-w-md rounded-sm" data-testid="send-email-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-base uppercase">Enviar Email al Cliente</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {(() => {
              // Iter 152 — email robusto: probar varios campos legacy y validar formato.
              const email = (f.client_email || apt.email || apt.customer_email || apt.contact_email || "").trim();
              const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
              if (!email) {
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="email-missing-warning">
                    <strong>Esta cita no tiene email de cliente.</strong>
                    <p className="mt-1">Cierra este diálogo, edita el campo "Email" en el detalle de la cita, guarda y vuelve a intentarlo.</p>
                  </div>
                );
              }
              if (!valid) {
                return (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-900" data-testid="email-invalid-warning">
                    <strong>Email no válido:</strong> {email}
                    <p className="mt-1">Corrige el formato en el campo "Email" del detalle, guarda y vuelve a intentarlo.</p>
                  </div>
                );
              }
              return <p className="text-xs text-slate-500">Se enviará a: <strong>{email}</strong></p>;
            })()}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Asunto</Label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Ej: Informacion sobre su vehiculo" className="rounded-sm h-8 text-sm" data-testid="email-subject" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Mensaje</Label>
              <Textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Escriba su mensaje..." className="rounded-sm text-sm h-32 resize-none" data-testid="email-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !(/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((f.client_email || apt.email || apt.customer_email || apt.contact_email || "").trim()))}
              className="bg-blue-600 hover:bg-blue-700 rounded-sm text-xs uppercase font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-send-btn"
            >
              <Mail className="w-3 h-3 mr-1" /> {sendingEmail ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <RescheduleModal
        open={showReschedule}
        onClose={() => setShowReschedule(false)}
        apt={apt}
        axiosAuth={axiosAuth}
        onRescheduled={() => {
          // Cierra el detail dialog también — el padre refrescará el listado.
          onClose && onClose();
        }}
      />
    </div>
  );
}
